/**
 * Portfolio calculation utilities for consistent percentage and dollar calculations
 * with proper rounding and precision handling
 */

export interface AllocationData {
  id?: string; // Database ID for existing allocations
  allocation_percentage: number;
  allocation_value?: number;
  dollar_amount?: number;
  is_value_locked?: boolean;
}

export interface PortfolioTotals {
  totalDollarAmount: number;
  totalPercentage: number;
  isFullyAllocated: boolean;
  hasOverflow: boolean;
  overflowAmount: number;
}

/**
 * UNIFIED PORTFOLIO CALCULATION - Single Source of Truth
 * This function replaces the separate calculateTotalDollarAmount and calculateTotalPercentage functions
 * to ensure consistent calculations across the entire application.
 */
/**
 * @deprecated Use calculatePortfolioTotalsByMode instead. This legacy helper mixes
 * percentage/dollar logic via is_value_locked and should be removed after all callers
 * migrate to mode-aware calculations.
 */
export const calculatePortfolioTotals = (
  allocations: AllocationData[],
  portfolioSize: number,
  tolerance: number = 0.01
): PortfolioTotals => {
  // Edge case: Handle zero or negative portfolio size
  if (portfolioSize <= 0) {
    const totalPercentage = allocations.reduce((sum, allocation) => sum + (allocation.allocation_percentage || 0), 0);
    return {
      totalDollarAmount: 0,
      totalPercentage,
      isFullyAllocated: Math.abs(totalPercentage - 100) <= tolerance,
      hasOverflow: totalPercentage > 100 + tolerance,
      overflowAmount: Math.max(0, totalPercentage - 100)
    };
  }

  let totalDollarAmount = 0;
  let totalPercentage = 0;

  allocations.forEach((allocation: AllocationData) => {
    // Determine if this allocation is locked to a dollar value
    const isDollarLocked = !!(allocation.allocation_value || allocation.is_value_locked);
    
    if (isDollarLocked) {
      // Dollar-locked allocation: use allocation_value as primary, calculate percentage
      const dollarValue = Math.max(0, allocation.allocation_value || 0); // Bounds check
      const percentageValue = (dollarValue / portfolioSize) * 100;
      
      totalDollarAmount += dollarValue;
      totalPercentage += percentageValue;
    } else {
      // Percentage-driven allocation: use allocation_percentage as primary, calculate dollar
      const percentageValue = Math.max(0, Math.min(1000, allocation.allocation_percentage || 0)); // Bounds: 0-1000%
      const dollarValue = (percentageValue / 100) * portfolioSize;
      
      totalDollarAmount += dollarValue;
      totalPercentage += percentageValue;
    }
  });

  // Round to handle floating point precision
  totalPercentage = Math.round(totalPercentage * 100) / 100;
  totalDollarAmount = Math.round(totalDollarAmount * 100) / 100;

  const isFullyAllocated = Math.abs(totalPercentage - 100) < tolerance;
  const hasOverflow = totalDollarAmount > portfolioSize;
  const overflowAmount = hasOverflow ? totalDollarAmount - portfolioSize : 0;

  return {
    totalDollarAmount,
    totalPercentage,
    isFullyAllocated,
    hasOverflow,
    overflowAmount
  };
};


export type CalculationMode = 'percentage' | 'dollar' | 'auto';

/**
 * Type-aware portfolio totals calculation.
 * - mode 'percentage': percentages are authoritative; dollars are derived.
 * - mode 'dollar': dollars are authoritative (allocation_value); percentages are derived.
 * - mode 'auto': fallback to legacy behavior (lock-based) using calculatePortfolioTotals.
 */
export const calculatePortfolioTotalsByMode = (
  allocations: AllocationData[],
  portfolioSize: number,
  mode: CalculationMode,
  tolerance: number = 0.01
): PortfolioTotals => {
  if (mode === 'auto') {
    // Delegate to legacy function for backward compatibility
    return calculatePortfolioTotals(allocations, portfolioSize, tolerance);
  }

  // Handle zero or negative size safely
  if (portfolioSize <= 0) {
    const totalPercentage = mode === 'percentage'
      ? allocations.reduce((sum, a) => sum + (a.allocation_percentage || 0), 0)
      : 0; // in dollar mode, with non-positive size, treat utilization as 0

    return {
      totalDollarAmount: 0,
      totalPercentage: Math.round(totalPercentage * 100) / 100,
      isFullyAllocated: Math.abs(totalPercentage - 100) <= tolerance,
      hasOverflow: false,
      overflowAmount: 0,
    };
  }

  let totalDollarAmount = 0;
  let totalPercentage = 0;

  allocations.forEach((allocation) => {
    if (mode === 'percentage') {
      const percentageValue = Math.max(0, Math.min(1000, allocation.allocation_percentage || 0));
      const dollarValue = (percentageValue / 100) * portfolioSize;
      totalDollarAmount += dollarValue;
      totalPercentage += percentageValue;
    } else if (mode === 'dollar') {
      const dollarValue = Math.max(0, allocation.allocation_value ?? 0);
      const percentageValue = (dollarValue / portfolioSize) * 100;
      totalDollarAmount += dollarValue;
      totalPercentage += percentageValue;
    }
  });

  totalPercentage = Math.round(totalPercentage * 100) / 100;
  totalDollarAmount = Math.round(totalDollarAmount * 100) / 100;

  // In dollar mode, use dollar equality (within cents) as the primary check for "fully allocated"
  const isFullyAllocated = mode === 'dollar'
    ? Math.abs(totalDollarAmount - portfolioSize) < 0.01
    : Math.abs(totalPercentage - 100) < tolerance;
  const hasOverflow = totalDollarAmount > portfolioSize + tolerance;
  const overflowAmount = hasOverflow ? totalDollarAmount - portfolioSize : 0;

  return {
    totalDollarAmount,
    totalPercentage,
    isFullyAllocated,
    hasOverflow,
    overflowAmount,
  };
};


/**
 * Check if portfolio is fully allocated (within tolerance)
 */
export const isFullyAllocated = (totalPercentage: number, tolerance: number = 0.01): boolean => {
  return Math.abs(totalPercentage - 100) < tolerance;
};

/**
 * Format currency with proper formatting - SINGLE SOURCE OF TRUTH
 */
export const formatCurrency = (
  amount: number, 
  currency?: string,
  options: {
    showDecimals?: boolean;
    locale?: string;
    showCurrency?: boolean;
  } = {}
): string => {
  const { showDecimals = true, locale = 'en-US', showCurrency = false } = options; 
  
  // Map ISO currency codes to locales that prefer symbol-only (no country prefix)
  const mapCurrencyToLocale = (code: string): string => {
    switch (code.toUpperCase()) {
      case 'CAD': return 'en-CA';
      case 'USD': return 'en-US';
      case 'EUR': return 'en-IE';
      case 'GBP': return 'en-GB';
      case 'AUD': return 'en-AU';
      case 'NZD': return 'en-NZ';
      case 'JPY': return 'ja-JP';
      case 'CHF': return 'de-CH';
      default: return 'en-US';
    }
  };
  
  // Handle null, undefined, NaN, or invalid values
  const numValue = Number(amount);
  let safeValue = Number.isFinite(numValue) ? numValue : 0;
  
  // Fix floating point precision issues by rounding to 2 decimal places
  safeValue = Math.round(safeValue * 100) / 100;

  // If a currency code is provided, prefer Intl currency formatting with symbol localization
  if (currency) {
    const code = currency.toUpperCase();
    try {
      const effectiveLocale = options.locale || mapCurrencyToLocale(code);
      return new Intl.NumberFormat(effectiveLocale, {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0,
      }).format(safeValue);
    } catch {
      // Fallback for non-ISO codes (e.g., BTC/ETH): prefix code and format numerically
      const formattedNumber = new Intl.NumberFormat(locale, {
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0,
      }).format(safeValue);
      return `${code} ${formattedNumber}`;
    }
  }

  // No currency provided: preserve prior behavior of a plain $ prefix
  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(safeValue);
  return `$${formattedNumber}`;
};

/**
 * Parse currency string to number (removes $ and commas)
 */
export const parseCurrency = (value: string): number => {
  const cleaned = value.replace(/[^0-9]/g, '');
  return Number(cleaned) || 0;
};

/**
 * Synchronize allocation states when portfolio size changes
 * For locked allocations: keep dollar value, recalculate percentage
 * For unlocked allocations: keep percentage, recalculate dollar value
 */
export const syncAllocationsForPortfolioSizeChange = (
  allocations: AllocationData[],
  newPortfolioSize: number
): AllocationData[] => {
  if (newPortfolioSize <= 0) {
    console.warn('Invalid portfolio size for allocation sync:', newPortfolioSize);
    return allocations;
  }

  return allocations.map((allocation) => {
    const isDollarLocked = !!(allocation.allocation_value || allocation.is_value_locked);
    
    if (isDollarLocked) {
      // Locked allocation: keep dollar value, recalculate percentage
      const dollarValue = allocation.allocation_value || 0;
      const newPercentage = (dollarValue / newPortfolioSize) * 100;
      
      return {
        ...allocation,
        allocation_percentage: Math.round(newPercentage * 100) / 100,
        allocation_value: dollarValue // Keep locked dollar value
      };
    } else {
      // Unlocked allocation: keep percentage, recalculate dollar (but don't lock)
      const percentage = allocation.allocation_percentage || 0;
      const newDollarValue = (percentage / 100) * newPortfolioSize;
      
      return {
        ...allocation,
        allocation_percentage: percentage, // Keep percentage
        dollar_amount: Math.round(newDollarValue * 100) / 100 // Update calculated dollar amount
      };
    }
  });
};

/**
 * Ensure allocation state consistency - synchronize percentage and dollar values
 * This should be called whenever an allocation is updated to prevent state divergence
 */
/**
 * @deprecated Prefer mode-driven editing and server-calculated values.
 * This function was used to "sync" allocation fields when switching lock state.
 * With portfolio-type simplification, per-allocation lock toggling is removed.
 */
export const synchronizeAllocationState = (
  allocation: AllocationData,
  portfolioSize: number,
  lockToDollar: boolean = false
): AllocationData => {
  if (portfolioSize <= 0) {
    console.warn('Invalid portfolio size for allocation sync:', portfolioSize);
    return allocation;
  }

  if (lockToDollar || allocation.allocation_value || allocation.is_value_locked) {
    // Dollar-locked: use dollar value as source of truth
    const dollarValue = allocation.allocation_value || 0;
    const calculatedPercentage = (dollarValue / portfolioSize) * 100;
    
    return {
      ...allocation,
      allocation_value: Math.round(dollarValue * 100) / 100,
      allocation_percentage: Math.round(calculatedPercentage * 100) / 100,
      is_value_locked: true
    };
  } else {
    // Percentage-driven: use percentage as source of truth
    const percentage = allocation.allocation_percentage || 0;
    const calculatedDollar = (percentage / 100) * portfolioSize;
    
    return {
      ...allocation,
      allocation_percentage: Math.round(percentage * 100) / 100,
      dollar_amount: Math.round(calculatedDollar * 100) / 100,
      allocation_value: null, // Clear locked value
      is_value_locked: false
    };
  }
};
